import { Link, useLocation, Navigate, Routes, Route } from 'react-router-dom';
import { Users, BarChart2, Settings, Shield, Activity, Database, CreditCard, ArrowLeft, LogOut } from 'lucide-react';
import { useAdmin } from '../../hooks/useAdmin';
import { useState, useEffect } from 'react';
import { supabase } from '../../services/supabase';
import { AdminDashboard } from './AdminDashboard';
import { UsersPage } from './UsersPage';
import { AnalyticsPage } from './AnalyticsPage';
import { auth } from '../../services/auth';

const navigation = [
  { name: 'Dashboard', path: '/admin', icon: BarChart2 },
  { name: 'Users', path: '/admin/users', icon: Users },
  { name: 'Analytics', path: '/admin/analytics', icon: Activity },
  { name: 'Subscriptions', path: '/admin/subscriptions', icon: CreditCard },
  { name: 'Database', path: '/admin/database', icon: Database },
  { name: 'Settings', path: '/admin/settings', icon: Settings },
];

export function AdminLayout() {
  const location = useLocation();
  const { isAdmin, loading } = useAdmin();
  const [userName, setUserName] = useState('Admin');
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    loadUserProfile();
  }, []);

  async function loadUserProfile() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name, full_name, email')
        .eq('id', user.id)
        .maybeSingle();

      if (profile) {
        setUserName(profile.display_name || profile.full_name || 'Admin');
        setUserEmail(profile.email || user.email || '');
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }

  async function handleSignOut() {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex h-screen bg-paper">
      {/* Sidebar */}
      <div className="w-64 bg-gradient-to-b from-ink-950 to-ink-900 border-r border-gold-500/10 flex flex-col">
        <div className="p-6 flex-1">
          <div className="flex items-center space-x-2 text-gold-500 mb-8">
            <Shield className="h-6 w-6" />
            <span className="text-lg font-heading font-semibold">Admin Panel</span>
          </div>

          {/* Back to App Link */}
          <Link
            to="/app"
            className="flex items-center space-x-3 px-4 py-3 mb-6 rounded-lg bg-gold-500/10 text-gold-500 hover:bg-gold-500/20 transition-colors group"
          >
            <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to App</span>
          </Link>

          <nav className="space-y-1">
            {navigation.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-gold-500 text-ink-950'
                      : 'text-gray-300 hover:bg-ink-800 hover:text-gold-500'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* User Profile Section */}
        <div className="p-4 border-t border-gold-500/10">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 bg-gold-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-ink-950 font-bold text-sm">
                {userName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{userName}</p>
              <p className="text-xs text-gray-400 truncate">{userEmail}</p>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span className="text-sm font-medium">Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </div>
    </div>
  );
}